import { useState, useCallback } from 'react';
import { checkPlatformConsentStatus, recordPlatformConsent, getPlatformPolicies, PlatformConsentStatus, PlatformPolicy } from '../services/consentApi';

interface UseReconsentReturn {
  isLoading: boolean;
  requiresReconsent: boolean;
  consentStatus: PlatformConsentStatus | null;
  policies: PlatformPolicy[];
  error: string | null;
  checkReconsentNeeded: (email: string) => Promise<boolean>;
  submitReconsent: (email: string, privacyAccepted: boolean, termsAccepted: boolean) => Promise<boolean>;
  dismissReconsent: () => void;
}

/**
 * Hook for managing platform re-consent requirements.
 * 
 * Use this hook after login to check if the user needs to re-consent
 * to updated privacy policy or terms of service.
 * 
 * Example usage:
 * ```tsx
 * const { requiresReconsent, consentStatus, policies, submitReconsent } = useReconsent();
 * 
 * useEffect(() => {
 *   if (userEmail) {
 *     checkReconsentNeeded(userEmail);
 *   }
 * }, [userEmail]);
 * 
 * if (requiresReconsent) {
 *   return <ReconsentPopup policies={policies} onSubmit={submitReconsent} />;
 * }
 * ```
 */
export function useReconsent(): UseReconsentReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [requiresReconsent, setRequiresReconsent] = useState(false);
  const [consentStatus, setConsentStatus] = useState<PlatformConsentStatus | null>(null);
  const [policies, setPolicies] = useState<PlatformPolicy[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if the user needs to re-consent to platform policies.
   * Call this after successful login.
   */
  const checkReconsentNeeded = useCallback(async (email: string): Promise<boolean> => {
    if (!email) {
      setError('Email is required to check consent status');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check consent status
      const status = await checkPlatformConsentStatus(email);
      setConsentStatus(status);

      if (status.requiresReconsent) {
        // Load current policies for display
        const currentPolicies = await getPlatformPolicies();
        setPolicies(currentPolicies);
        setRequiresReconsent(true);
        return true;
      }

      setRequiresReconsent(false);
      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check consent status';
      setError(message);
      console.error('Reconsent check failed:', err);
      // Don't block login on error - just log it
      setRequiresReconsent(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Submit re-consent for platform policies.
   * Returns true on success, false on failure.
   */
  const submitReconsent = useCallback(async (
    email: string,
    privacyAccepted: boolean,
    termsAccepted: boolean
  ): Promise<boolean> => {
    if (!email) {
      setError('Email is required');
      return false;
    }

    if (!privacyAccepted || !termsAccepted) {
      setError('You must accept both the Privacy Policy and Terms of Service to continue');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await recordPlatformConsent({
        userEmail: email,
        privacyPolicyAccepted: privacyAccepted,
        termsOfServiceAccepted: termsAccepted,
        deviceInfo: navigator.userAgent,
      });

      setRequiresReconsent(false);
      setConsentStatus(null);
      setPolicies([]);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to record consent';
      setError(message);
      console.error('Reconsent submission failed:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Dismiss re-consent popup (user chose to logout instead).
   */
  const dismissReconsent = useCallback(() => {
    setRequiresReconsent(false);
    setConsentStatus(null);
    setPolicies([]);
    setError(null);
  }, []);

  return {
    isLoading,
    requiresReconsent,
    consentStatus,
    policies,
    error,
    checkReconsentNeeded,
    submitReconsent,
    dismissReconsent,
  };
}

export default useReconsent;
